<template>
  <div>
    <!-- Cancelled order -->
    <div v-if="isCancelled">
      <portal target-el="#filter">
        <Breadcrumb :isHistoryBack="true" :hideBackBtn="true" :message="details.order" :status="details.status" :indentStatus="details.indentStatus" />
      </portal>
      <div class="status-msg">
        <span>
          <svgicon name="shipment-cancel" width="300" height="300" :original="true" />
          <p>Your order has been cancelled. Please contact support for further updates.</p>
        </span>
      </div>
    </div>

    <!-- Main tracking view -->
    <div v-else class="order-wrapper primary-disabled">
      <portal target-el="#filter">
        <Breadcrumb :isHistoryBack="true" :hideBackBtn="true" :message="normalized.shipmentNumber" :status="normalized.status" :indentStatus="details.indentStatus" />
      </portal>

      <div v-if="details.error" class="status-msg">
        <span>
          <svgicon name="empty-timeline" width="249" height="176" :original="true" />
          <p>No Data Found</p>
        </span>
      </div>

      <div v-else-if="isLoading" class="loading-app">
        <Spinner />
      </div>

      <Row v-else :gutter="20" class="row-reverse">
        <Col :xl="6" :lg="6" :md="8">
          <div class="slider-collapse wbview">
            <div class="drawer-header slider-collapse-header" @click="active = !active">
              <span class="status status-icon status-grey">
                {{status.name}}
                <span class="badge" v-if="trackingType && trackingType.iconName">
                  <svgicon :name="trackingType.iconName" width="8" height="8" />
                </span>
              </span>
              <span class="icon-mv">
                <svgicon name="icon-add-dark" v-if="active" width="16" height="16" :original="true"/>
                <svgicon name="icon-minus-dark" v-else width="16" height="16" :original="true"/>
              </span>
            </div>
            <div class="drawer-body slider-collapse-body" :class="{ showview: active }">
              <ul class="drawer-list">
                <li v-for="header in trackDetailsConstant" :key="header.id">
                  <span v-if="trackDetail[header.column]">
                    <label>{{header.title}}</label>
                    <div class="list-content">
                      {{trackDetail[header.column]}}
                      <small
                        v-if="header.subDetail && trackDetail[header.subDetail.column]"
                        :class="header.subDetail.class"
                      >
                      {{trackDetail[header.subDetail.column]}}
                      </small>
                    </div>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </Col>
        <Col :xl="18" :lg="18" :md="16">
        <ElTabs class="tab-default">
          <Tab-pane label="Track">
            <LocationTab
              :pings="normalized.pings"
              :pickupLocations="normalized.pickupLocations"
              :dropLocations="normalized.dropLocations"
              :isMapView="normalized.isMapView"
              :latestLocation="normalized.lastKnownLocation"
              :trackingType="normalized.trackingType"
              :configurations="configurations"
              :environmentVariables="environmentVariables"
              :canAutoRefresh="normalized.canAutoRefresh"
              :hasCourierData="hasCourierData"
              :mapLoading="mapLoading"
              :isOpenLink="isOpenLink"
              :overrideMessage="normalized.overrideMessage"
              @refresh="refreshLocation"
            />
          </Tab-pane>

          <Tab-pane v-if="normalized.viewMaterials" label="Materials">
            <MaterialsTab
              :materials="normalized.materials"
              :hasMaterials="normalized.hasMaterials"
            />
          </Tab-pane>

          <Tab-pane label="Timeline" v-if="!normalized.overrideMessage">
            <TimelineTab
              :milestoneEvents="normalized.milestoneEvents"
              :courierTimeline="normalized.courierTimeline"
              :tripHalts="normalized.tripHalts"
              :midwayChanges="normalized.midwayChanges"
              :endTripDetails="normalized.endTripDetails"
              :pings="normalized.pings"
              :trackingType="normalized.trackingType"
              :customerReferenceMap="normalized.customerReferenceMap"
              :createdAt="normalized.createdAt"
              :pickupLocations="normalized.pickupLocations"
              :dropLocations="normalized.dropLocations"
              :entityType="details.entityType"
              :timeFormat="timeFormat"
              :isDelivered="!!normalized.overrideMessage"
            />
          </Tab-pane>
        </ElTabs>
        </Col>
      </Row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getAddress } from '@common/lib/track';
import { trackDetailsConstant, trackStatus, trackType, endTripMessage } from '@/constants/trackConstants';
import { formatLastKnownTime, getDateDiffInDaysMins, formatDetailedTime } from '@/lib/Date';
import moment from 'moment';
import Breadcrumb from '@/components/common/Breadcrumb';
import LocationTab from './LocationTab';
import MaterialsTab from './MaterialsTab';
import TimelineTab from './TimelineTab';

const TERMINAL_STATUSES = ['COMPLETED', 'CANCELLED', 'INVALID', 'AUTO COMPLETED'];

export default {
  name: 'UnifiedTracking',
  components: { Breadcrumb, LocationTab, MaterialsTab, TimelineTab },
  props: {
    isTrackLink: { type: Boolean, default: false },
    isOpenLink: { type: Boolean, default: false },
  },
  data() {
    return {
      trackDetailsConstant,
      details: {},
      mapLoading: false,
      autoRefreshTimer: null,
      active: false,
    };
  },
  async mounted() {
    this.$store.commit('track/initialReset');
    if (this.isTrackLink) {
      await this.fetchTrackLinkData();
    } else if (this.isOpenLink) {
      await this.fetchOpenLinkData();
    }
    if (this.details && !this.details.error && this.normalized.canAutoRefresh) {
      this.startAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  computed: {
    ...mapGetters({
      isLoading: 'track/isLoading',
      configurations: 'app/configurations',
      environmentVariables: 'app/environmentVariables',
    }),
    isCancelled() {
      return this.isTrackLink && this.details.indentStatus === 8;
    },
    hasCourierData() {
      return !!(this.details.courier_timeline && this.details.courier_timeline.length);
    },
    timeFormat() {
      return this.configurations && this.configurations.is_datepicker_24H_enabled ? 'HH:mm' : 'hh:mm A';
    },
    status() {
      if (this.normalized.overrideMessage) return { name: 'DELIVERED', colorName: 'status-green' };
      return trackStatus.find(val => val.value === this.normalized.status) || {};
    },
    trackingType() {
      return trackType.find(val => val.value === this.normalized.trackingType);
    },
    trackDetail() {
      const n = this.normalized;
      const obj = {
        shipmentNumber: n.shipmentNumber,
        transporterName: n.transporterName,
        eta: formatDetailedTime(n.plannedEta),
        createdAt: formatDetailedTime(n.createdAt),
        driverNumber: n.driverPhoneNumber || '-',
        driverName: n.driverName || '-',
        truckNumber: n.vehicleNumber || '-',
        isGpsEnabled: n.trackingType === 'GPS' ? 'GPS ENABLED' : '',
        source: n.source,
        sourceAddress: n.sourceAddress,
        destination: n.destination,
        destinationAddress: n.destinationAddress,
      };
      const actualEtaAvl = n.actualEta && n.actualEta !== 'NA';
      obj.actualEta = actualEtaAvl ? formatDetailedTime(n.actualEta) : '-';
      const etaDiff = actualEtaAvl && getDateDiffInDaysMins(n.actualEta, moment());
      obj.etaDetail = this.status && !this.status.excludeEta ? etaDiff && etaDiff.formatted : null;
      if (n.lastKnownLocation) {
        obj.lastKnownPlace = n.lastKnownLocation.city || n.lastKnownLocation.address;
        obj.lastKnownTime = formatLastKnownTime(n.lastKnownLocation.timeStamp);
      }
      return obj;
    },
    normalized() {
      if (!this.details || this.details.error) return this.emptyNormalized();
      return this.isTrackLink ? this.normalizeFlow1(this.details) : this.normalizeFlow2(this.details);
    },
  },
  methods: {
    // --- Data fetching ---
    async fetchTrackLinkData() {
      const orderNo = this.$route.params.orderNo;
      const consigneeId = this.$route.query.id;
      const bookingId = this.$route.query.bookingId;
      this.details = await this.$store.dispatch('track/getConsigneeTripDetails', { orderNo, consigneeId, bookingId });
      if (this.details && this.details.error) {
        this.$errorMessage({ message: this.details.error });
      }
    },
    async fetchOpenLinkData() {
      const id = this.$route.query.id;
      const t = this.$route.query.t;
      this.details = await this.$store.dispatch('track/getTripDetails', { id, t, isOpenLink: true });
      if (this.details && this.details.error) {
        this.$errorMessage({ message: this.details.error });
      }
    },

    // --- Auto-refresh ---
    async refreshLocation() {
      if (this.isLoading || this.mapLoading) return;
      if (TERMINAL_STATUSES.includes(this.normalized.status)) {
        this.stopAutoRefresh();
        return;
      }
      this.mapLoading = true;
      let response;
      if (this.isTrackLink && this.details.id) {
        const consigneeId = this.$route.query.id;
        const bookingId = this.$route.query.bookingId;
        response = await this.$store.dispatch('track/getShipmentTrackLocations', { id: this.details.id, consigneeId, bookingId });
      } else if (this.isOpenLink) {
        const id = this.$route.query.id;
        const t = this.$route.query.t;
        response = await this.$store.dispatch('track/getShipmentTrackLocations', { id, t, isOpenLink: true });
      }
      if (response) {
        if (response.pings) this.$set(this.details, 'pings', response.pings);
        if (response.latestLocation) this.$set(this.details, 'latestLocation', response.latestLocation);
        if (response.dropLocations) this.$set(this.details, 'dropLocations', response.dropLocations);
        if (response.pickupLocations) this.$set(this.details, 'pickupLocations', response.pickupLocations);
        if (response.actualEta) this.$set(this.details, 'actualEta', response.actualEta);
        if (response.status) this.$set(this.details, 'status', response.status);
        if (response.viewMaterials !== undefined) this.$set(this.details, 'viewMaterials', response.viewMaterials);
        // Handle mid-session delivery completion
        if (response.overrideMessage) {
          this.$set(this.details, 'overrideMessage', response.overrideMessage);
          this.stopAutoRefresh();
        }
      }
      this.mapLoading = false;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      if (!TERMINAL_STATUSES.includes(this.normalized.status)) {
        this.autoRefreshTimer = setInterval(this.refreshLocation, 60000);
      }
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },

    // --- Normalization ---
    emptyNormalized() {
      return {
        shipmentNumber: '',
        status: '',
        transporterName: '',
        vehicleNumber: '',
        driverName: '',
        driverPhoneNumber: '',
        plannedEta: null,
        actualEta: null,
        createdAt: null,
        source: '',
        sourceAddress: '',
        destination: '',
        destinationAddress: '',
        lastKnownLocation: null,
        trackingType: null,
        isMapView: false,
        pings: [],
        pickupLocations: [],
        dropLocations: [],
        canAutoRefresh: false,
        overrideMessage: null,
        viewMaterials: false,
        materials: [],
        hasMaterials: false,
        courierTimeline: [],
        milestoneEvents: [],
        tripHalts: [],
        midwayChanges: [],
        endTripDetails: [],
        customerReferenceMap: {},
      };
    },
    endtripConstruction(d) {
      const actor = (this.details.entityType && this.details.entityType !== 'NA') ? this.details.entityType : 'System';
      const reason = endTripMessage.find(r => r.id === d.reason);
      return reason ? `${reason.message.replace('{actor}', actor).replace('#customerReference#', d.customerReference || '')} on ${formatDetailedTime(d.timestamp)}` : 'This trip has been completed';
    },
    normalizeFlow1(d) {
      const firstPick = d.pickupLocations && d.pickupLocations[0];
      const lastDrop = d.dropLocations && d.dropLocations[d.dropLocations.length - 1];
      const isDelivered = !!d.overrideMessage;
      const isTripEnded = !isDelivered && d.endTripDetails && d.endTripDetails.length > 0;
      const endTripMsg = isTripEnded && this.endtripConstruction(d.endTripDetails[0]);
      const overrideMessage =  endTripMsg ? endTripMsg : d.overrideMessage ? `${d.overrideMessage} ${formatDetailedTime(d.shipmentDeliveryTime)}` : '';
      return {
        shipmentNumber: d.shipmentNumber || d.order,
        status: d.status,
        transporterName: d.transporterName,
        vehicleNumber: d.truck_number,
        driverName: d.driverName,
        driverPhoneNumber: d.driverPhoneNumber,
        plannedEta: lastDrop && lastDrop.plannedArrival ? lastDrop.plannedArrival : d.plannedEta,
        actualEta: lastDrop && lastDrop.estimatedArrival ? lastDrop.estimatedArrival : d.actualEta,
        createdAt: d.createdAt,
        source: firstPick && firstPick.address && firstPick.address.name,
        sourceAddress: firstPick && firstPick.address && getAddress(firstPick.address),
        destination: lastDrop && lastDrop.address && lastDrop.address.name,
        destinationAddress: lastDrop && lastDrop.address && getAddress(lastDrop.address),
        lastKnownLocation: d.latestLocation,
        trackingType: d.typeOfTracking,
        isMapView: !!d.is_map_view,
        pings: d.pings || [],
        pickupLocations: d.pickupLocations || [],
        dropLocations: d.dropLocations || [],
        canAutoRefresh: !isDelivered && !!d.id,
        overrideMessage,
        shipmentDeliveryTime: d.shipmentDeliveryTime || null,
        viewMaterials: d.viewMaterials !== undefined ? !!d.viewMaterials : true,
        materials: d.materials || [],
        hasMaterials: true,
        courierTimeline: d.courier_timeline || [],
        milestoneEvents: [],
        tripHalts: [],
        midwayChanges: [],
        endTripDetails: [],
        customerReferenceMap: {},
      };
    },
    normalizeFlow2(d) {
      const firstPick = d.firstPickup || (d.pickupLocations && d.pickupLocations[0]);
      const lastDrop = d.lastDrop || (d.dropLocations && d.dropLocations[d.dropLocations.length - 1]);
      const isDelivered = !!d.overrideMessage;
      const isTripEnded = !isDelivered && d.endTripDetails && d.endTripDetails.length > 0;
      const endTripMsg = isTripEnded && this.endtripConstruction(d.endTripDetails[0]);
      const overrideMessage =  endTripMsg ? endTripMsg : d.overrideMessage ? `${d.overrideMessage} ${formatDetailedTime(d.shipmentDeliveryTime)}` : '';
      return {
        shipmentNumber: d.shipmentNumber,
        status: d.status,
        transporterName: d.transporterName,
        vehicleNumber: d.truck_number,
        driverName: d.driverName,
        driverPhoneNumber: d.driverPhoneNumber,
        plannedEta: lastDrop && lastDrop.plannedArrival ? lastDrop.plannedArrival : d.plannedEta,
        actualEta: lastDrop && lastDrop.estimatedArrival ? lastDrop.estimatedArrival : d.actualEta,
        createdAt: d.createdAt,
        source: firstPick && firstPick.address && firstPick.address.name,
        sourceAddress: firstPick && firstPick.address && getAddress(firstPick.address),
        destination: lastDrop && lastDrop.address && lastDrop.address.name,
        destinationAddress: lastDrop && lastDrop.address && getAddress(lastDrop.address),
        lastKnownLocation: d.latestLocation,
        trackingType: d.typeOfTracking,
        isMapView: true,
        pings: d.pings || [],
        pickupLocations: d.pickupLocations || [],
        dropLocations: d.dropLocations || [],
        canAutoRefresh: !isDelivered && !!d.id,
        overrideMessage,
        shipmentDeliveryTime: d.shipmentDeliveryTime || null,
        viewMaterials: d.viewMaterials !== undefined ? !!d.viewMaterials : true,
        materials: d.materials || [],
        hasMaterials: !!(d.materials && d.materials.length),
        courierTimeline: [],
        milestoneEvents: [],
        tripHalts: d.tripHalts || [],
        midwayChanges: d.midwayChangeData || [],
        endTripDetails: d.endTripDetails || [],
        customerReferenceMap: d.customerReferenceMap || {},
      };
    },
  },
};
</script>

<style scoped>
.delivery-completed-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background-color: #e6f7e6;
  border: 1px solid #b7e1b7;
  border-radius: 4px;
  font-size: 14px;
  color: #2e7d32;
}
</style>
